/**
 * @fileoverview Syntax highlighting for fenced code, as a renderer feature.
 *
 * THE HIGHLIGHTER EMITS CLASSES AND THE THEME SUPPLIES THE COLOURS. A span
 * carries `hljs-keyword` and nothing else; what colour a keyword is comes from
 * the `code-*` tokens in `page.css`, so highlighting is themed like everything
 * else and no colour is hardcoded into page content.
 *
 * A SMALL, EXPLICIT LANGUAGE SET. Every grammar is bundle weight paid by every
 * reader, and the fleet writes in a handful of languages. An unknown language,
 * or none, is shown as plain escaped text, which is what it was before this.
 *
 * Pure string work: it runs headless, so the CLI and a future server render
 * produce the same markup the browser does.
 */

import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import diff from 'highlight.js/lib/languages/diff';
import dockerfile from 'highlight.js/lib/languages/dockerfile';
import go from 'highlight.js/lib/languages/go';
import ini from 'highlight.js/lib/languages/ini';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import python from 'highlight.js/lib/languages/python';
import rust from 'highlight.js/lib/languages/rust';
import shell from 'highlight.js/lib/languages/shell';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';

import { escapeHtml } from './directives';

const LANGUAGES = {
  bash,
  css,
  diff,
  dockerfile,
  go,
  ini,
  javascript,
  json,
  markdown,
  python,
  rust,
  shell,
  sql,
  typescript,
  xml,
  yaml,
};
for (const [name, grammar] of Object.entries(LANGUAGES)) hljs.registerLanguage(name, grammar);
hljs.registerAliases(['sh', 'zsh'], { languageName: 'bash' });
hljs.registerAliases(['console'], { languageName: 'shell' });
hljs.registerAliases(['js', 'jsx', 'mjs'], { languageName: 'javascript' });
hljs.registerAliases(['ts', 'tsx'], { languageName: 'typescript' });
hljs.registerAliases(['html', 'svg'], { languageName: 'xml' });
hljs.registerAliases(['yml'], { languageName: 'yaml' });
hljs.registerAliases(['toml'], { languageName: 'ini' });
hljs.registerAliases(['py'], { languageName: 'python' });
hljs.registerAliases(['md'], { languageName: 'markdown' });
hljs.registerAliases(['rs'], { languageName: 'rust' });

/** The info string's first word, if it is a plausible language name. */
function languageOf(info: string | undefined): string | null {
  const word = (info ?? '').trim().split(/\s+/)[0] ?? '';
  return /^[A-Za-z0-9+#-]{1,24}$/.test(word) ? word.toLowerCase() : null;
}

/** A fenced code block, highlighted where we know the language. */
export function renderCodeBlock(text: string, info: string | undefined): string {
  const lang = languageOf(info);
  const body = text.replace(/\n$/, '');
  const attr = lang ? ` data-lang="${escapeHtml(lang)}"` : '';
  if (lang && hljs.getLanguage(lang)) {
    // hljs escapes the source itself; its output is spans with class names
    // and the escaped text, which the sanitiser then checks again.
    const html = hljs.highlight(body, { language: lang, ignoreIllegals: true }).value;
    return `<pre class="pages-code"${attr}><code class="hljs language-${escapeHtml(lang)}">${html}</code></pre>\n`;
  }
  return `<pre class="pages-code"${attr}><code>${escapeHtml(body)}</code></pre>\n`;
}
